import axios from 'axios';
import config from '../config';
import { SecurityPolicy, DashboardStats } from '../types';

// API_BASE_URL을 환경 설정에서 가져옴
const API_BASE_URL = `${config.apiUrl}/api`;

// API 인스턴스 생성
const api = axios.create({
  baseURL: API_BASE_URL,
});

// 요청 인터셉터 - 디버그 로깅
api.interceptors.request.use((requestConfig) => {
  if (config.debug) {
    console.log(`📤 API Request: ${requestConfig.method?.toUpperCase()} ${requestConfig.url}`);
  }
  return requestConfig;
});

// 응답 인터셉터 - 에러 처리
api.interceptors.response.use(
  (response) => {
    if (config.debug) {
      console.log(`📥 API Response: ${response.status}`, response.data);
    }
    return response;
  },
  (error) => {
    console.error('❌ API Error:', error);
    if (error.response?.status === 401) {
      // 인증 실패 처리
      console.error('Unauthorized: 인증이 실패했습니다');
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const dashboardAPI = {
  getStats: async (): Promise<DashboardStats> => {
    if (config.useMockApi) {
      // Mock API 사용
      const response = await api.get('/dashboard/stats');
      return response.data;
    } else {
      // 실제 KubeArmor API 사용
      const { kubeArmorStatsAPI } = await import('./kubeArmorAdapter');
      return kubeArmorStatsAPI.getDashboard();
    }
  },
};

// Events
export const eventsAPI = {
  getAll: async (limit = 20, offset = 0) => {
    if (config.useMockApi) {
      const response = await api.get('/events', { params: { limit, offset } });
      return response.data;
    } else {
      const { kubeArmorEventsAPI } = await import('./kubeArmorAdapter');
      return kubeArmorEventsAPI.getAll(limit, offset);
    }
  },
  getBySeverity: async (severity: string) => {
    if (config.useMockApi) {
      const response = await api.get(`/events/severity/${severity}`);
      return response.data;
    } else {
      const { kubeArmorEventsAPI } = await import('./kubeArmorAdapter');
      return kubeArmorEventsAPI.getBySeverity(severity);
    }
  },
  getByNamespace: async (namespace: string) => {
    if (config.useMockApi) {
      const response = await api.get(`/events/namespace/${namespace}`);
      return response.data;
    } else {
      const { kubeArmorEventsAPI } = await import('./kubeArmorAdapter');
      return kubeArmorEventsAPI.getByNamespace(namespace);
    }
  },
};

// Policies
export const policiesAPI = {
  getAll: async (): Promise<SecurityPolicy[]> => {
    if (config.useMockApi) {
      const response = await api.get('/policies');
      return response.data.data;
    } else {
      const { kubeArmorPoliciesAPI } = await import('./kubeArmorAdapter');
      return kubeArmorPoliciesAPI.getAll();
    }
  },
  getById: async (id: string): Promise<SecurityPolicy> => {
    if (config.useMockApi) {
      const response = await api.get(`/policies/${id}`);
      return response.data;
    } else {
      const { kubeArmorPoliciesAPI } = await import('./kubeArmorAdapter');
      return kubeArmorPoliciesAPI.getById(id);
    }
  },
  getByNamespace: async (namespace: string) => {
    if (config.useMockApi) {
      const response = await api.get(`/policies/namespace/${namespace}`);
      return response.data;
    } else {
      const { kubeArmorPoliciesAPI } = await import('./kubeArmorAdapter');
      const all = await kubeArmorPoliciesAPI.getAll();
      return all.filter((p) => p.name?.includes(namespace));
    }
  },
  create: async (policy: Partial<SecurityPolicy>) => {
    if (config.useMockApi) {
      const response = await api.post('/policies', policy);
      return response.data;
    } else {
      const { kubeArmorPoliciesAPI } = await import('./kubeArmorAdapter');
      return kubeArmorPoliciesAPI.create(policy);
    }
  },
  update: async (id: string, policy: Partial<SecurityPolicy>) => {
    if (config.useMockApi) {
      const response = await api.put(`/policies/${id}`, policy);
      return response.data;
    } else {
      const { kubeArmorPoliciesAPI } = await import('./kubeArmorAdapter');
      return kubeArmorPoliciesAPI.update(id, policy);
    }
  },
  delete: async (id: string) => {
    if (config.useMockApi) {
      const response = await api.delete(`/policies/${id}`);
      return response.data;
    } else {
      const { kubeArmorPoliciesAPI } = await import('./kubeArmorAdapter');
      return kubeArmorPoliciesAPI.delete(id);
    }
  },
  toggleEnabled: async (id: string, enabled: boolean) => {
    if (config.useMockApi) {
      const response = await api.patch(`/policies/${id}`, { enabled });
      return response.data;
    } else {
      const { kubeArmorPoliciesAPI } = await import('./kubeArmorAdapter');
      return kubeArmorPoliciesAPI.toggleEnabled(id, enabled);
    }
  },
};

export default api;
